import { Instagram, Facebook } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/ui/tooltip';

/**
 * FooterSocialLinks Component
 * Minimal outline-style icons for footer - only Instagram and Facebook
 * Based on user's design reference - light grey outline icons
 * 
 * @param {Object} props
 * @param {Object} props.links - Social media URLs { instagramUrl, facebookUrl }
 */
export const FooterSocialLinks = ({ links = {} }) => {
  const { instagramUrl, facebookUrl } = links;
  
  // Check if any links exist (only Instagram and Facebook for footer)
  const hasLinks = instagramUrl || facebookUrl;
  
  if (!hasLinks) return null;

  const iconClass = "w-5 h-5 text-slate-400 hover:text-white transition-colors";

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-4">
        <span className="text-slate-500 text-sm">Follow us:</span>
        
        {instagramUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram className={iconClass} />
              </a>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-slate-800 border-slate-700">
              <p>Instagram</p>
            </TooltipContent>
          </Tooltip>
        )}
        
        {facebookUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform"
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <Facebook className={iconClass} />
              </a>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-slate-800 border-slate-700">
              <p>Facebook</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

/**
 * SocialLinks Component (Legacy - for areas that still need full social icons)
 * Uses react-social-icons for beautiful, standardized social media icons
 * All links open in new browser tabs with proper security attributes
 * 
 * @param {Object} props
 * @param {Object} props.links - Social media URLs { instagramUrl, facebookUrl, whatsappUrl }
 * @param {string} props.variant - 'footer' | 'header' for different styles
 * @param {boolean} props.showLabels - Show "Follow us:" label
 * @param {string} props.size - Icon size: 'sm' (32px), 'md' (40px), 'lg' (48px)
 */
export const SocialLinks = ({ 
  links = {}, 
  variant = 'footer',
  showLabels = false,
  size = 'md'
}) => {
  // For footer variant, use the new minimal FooterSocialLinks
  if (variant === 'footer') {
    return <FooterSocialLinks links={links} />;
  }
  
  // For other variants, keep the original implementation
  const { instagramUrl, facebookUrl, whatsappUrl } = links;
  
  // Check if any links exist
  const hasLinks = instagramUrl || facebookUrl || whatsappUrl;
  
  if (!hasLinks) return null;

  const iconClass = "w-5 h-5 text-slate-400 hover:text-white transition-colors";

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-3">
        {showLabels && <span className="text-slate-500 text-sm mr-1">Follow us:</span>}
        
        {instagramUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform"
                data-testid="social-instagram"
              >
                <Instagram className={iconClass} />
              </a>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-slate-800 border-slate-700">
              <p>Instagram</p>
            </TooltipContent>
          </Tooltip>
        )}
        
        {facebookUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform"
                data-testid="social-facebook"
              >
                <Facebook className={iconClass} />
              </a>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-slate-800 border-slate-700">
              <p>Facebook</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

/**
 * Floating WhatsApp Button
 * Fixed position button in bottom-right corner
 * Uses lucide icon for consistency
 */
export const FloatingWhatsApp = ({ url }) => {
  if (!url) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="fixed bottom-20 right-4 z-50 bg-green-500 hover:bg-green-400 p-3 rounded-full shadow-lg hover:scale-110 transition-all"
            data-testid="whatsapp-float-btn"
            aria-label="Contact us on WhatsApp"
          >
            <svg 
              className="w-5 h-5 text-white" 
              viewBox="0 0 24 24" 
              fill="currentColor"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </a>
        </TooltipTrigger>
        <TooltipContent side="left" className="bg-slate-800 border-slate-700">
          <p>Chat on WhatsApp</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default SocialLinks;
